'use client';

import React, { useCallback, useMemo, useState } from 'react';

import {
  CMS_IMAGE_MAX_WIDTH,
  getFullSizeCmsImageSrc,
  needsSharperIntrinsicImage,
} from '@/lib/images/cmsImage';
import { cn } from '@/lib/utils';

interface ImageItemProps {
  assetUrl: string;
  onClick?: () => void;
}

export const ImageItem: React.FC<ImageItemProps> = ({ assetUrl, onClick }) => {
  const imageItemClassname = cn(
    'rounded-[10px] shadow-post-shadow transition-opacity',
    'max-h-[267px] lg:max-h-[322px]',
    'block h-auto w-auto object-contain',
    onClick && 'cursor-pointer'
  );

  // Layout follows the file's intrinsic size (w-auto/h-auto), so this stays a plain
  // <img> without srcset: same pixels (capped at CMS_IMAGE_FULL_SIZE_WIDTH), unchanged
  // layout. Once loaded, a capped file that a dense screen paints larger than its
  // pixels is swapped for the widest variant; the browser keeps showing the current
  // image until the new one has decoded.
  const [sharperAssetUrl, setSharperAssetUrl] = useState<string | null>(null);
  const isSharper = sharperAssetUrl === assetUrl;
  const src = useMemo(
    () => getFullSizeCmsImageSrc(assetUrl, isSharper ? CMS_IMAGE_MAX_WIDTH : undefined),
    [assetUrl, isSharper]
  );

  const handleLoad = useCallback(
    (event: React.SyntheticEvent<HTMLImageElement>) => {
      const img = event.currentTarget;
      if (
        !isSharper &&
        needsSharperIntrinsicImage({
          naturalWidth: img.naturalWidth,
          renderedWidth: img.getBoundingClientRect().width,
          devicePixelRatio: window.devicePixelRatio,
        })
      ) {
        setSharperAssetUrl(assetUrl);
      }
    },
    [isSharper, assetUrl]
  );

  return (
    <img
      data-testid="BlockItem-Image"
      src={src}
      onClick={onClick}
      onLoad={handleLoad}
      loading="lazy"
      decoding="async"
      className={imageItemClassname}
    />
  );
};
